import React, { useState } from "react";
import Select from "react-select";
import {
  IconGripVertical,
  IconChevronUp,
  IconChevronDown,
  IconX,
} from "@tabler/icons-react";

const BrandSorter = ({
  disabled = false,
  brandOptions = [],
  brandsLoading,
  selectedCategory,
  categoryBrandPriorities,
  setCategoryBrandPriorities,
  prioritisedBrands,
  setPrioritisedBrands,
}) => {
  const [draggedItem, setDraggedItem] = useState(null);

  // Filter out already selected brands from options
  const availableOptions = brandOptions.filter(
    (option) => !prioritisedBrands?.some((item) => item.brand === option.value)
  );

  const addItem = (selectedOption) => {
    if (disabled || !selectedOption) return;
    setPrioritisedBrands([
      ...prioritisedBrands,
      {
        id: crypto.randomUUID(),
        category: selectedCategory?.value,
        brand: selectedOption.value,
        priority: prioritisedBrands?.length + 1,
      },
    ]);
    setCategoryBrandPriorities((prev) => {
      return [
        ...prev,
        {
          category: selectedCategory?.value,
          brand: selectedOption.value,
          priority: prioritisedBrands?.length + 1,
        },
      ];
    });
  };

  const removeItem = (removedItem) => {
    if (disabled) return;
    const newPrioritisedBrands = prioritisedBrands
      ?.filter((item) => item.id !== removedItem?.id)
      .map((item, index) => {
        return { ...item, priority: index + 1 };
      });
    setPrioritisedBrands(newPrioritisedBrands);
    setCategoryBrandPriorities((prev) => {
      return [
        ...prev.filter(
          (priority) => priority.category !== selectedCategory?.value
        ),
        ...newPrioritisedBrands,
      ];
    });
  };

  const moveItem = (fromIndex, toIndex) => {
    if (disabled) return;
    const newprioritisedBrands = [...prioritisedBrands];
    const [movedItem] = newprioritisedBrands?.splice(fromIndex, 1);
    newprioritisedBrands.splice(toIndex, 0, movedItem);
    newprioritisedBrands.forEach((item, index) => (item.priority = index + 1));
    setPrioritisedBrands(newprioritisedBrands);

    setCategoryBrandPriorities([
      ...categoryBrandPriorities?.filter(
        (brandPriority) => brandPriority.category !== selectedCategory?.value
      ),
      ...newprioritisedBrands,
    ]);
  };

  const moveUp = (e, index) => {
    e.preventDefault();

    if (disabled || index <= 0) return;
    moveItem(index, index - 1);
  };

  const moveDown = (e, index) => {
    e.preventDefault();

    if (disabled || index >= prioritisedBrands?.length - 1) return;
    moveItem(index, index + 1);
  };

  const handleDragStart = (e, index) => {
    if (disabled) {
      e.preventDefault();
      return;
    }
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (disabled || draggedItem === null || draggedItem === dropIndex) return;
    moveItem(draggedItem, dropIndex);
    setDraggedItem(null);
  };

  return (
    <div className="w-full mx-auto mb-2">
      <p className="mb-1 text-[13.6px] text-brand-grey-700">Brand Sorting</p>
      <p className="text-[12.8px] text-[#aaaaaa] mb-2 font-[300]">
        Sorts the appearance of brands for the selected category
      </p>

      <div className="mb-6">
        <Select
          options={availableOptions}
          onChange={addItem}
          placeholder="Select brands to sort"
          isSearchable={true}
          isClearable={true}
          isDisabled={!selectedCategory?.value || brandsLoading}
          value={null} // Always reset after selection
          noOptionsMessage={() => "No brands available"}
        />
      </div>

      <div className="space-y-2">
        {prioritisedBrands?.length > 0 &&
          prioritisedBrands?.map((item, index) => (
            <div
              key={item.id}
              draggable={!disabled}
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={handleDragOver}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={() => setDraggedItem(null)}
              className={`flex prioritisedBrands-center gap-3 p-3 rounded-sm border transition-all duration-200 ${
                disabled
                  ? "bg-gray-50 border-gray-200 opacity-60"
                  : draggedItem === index
                  ? "opacity-50 scale-95 bg-gray-50"
                  : "bg-gray-50 hover:bg-gray-100"
              }`}
            >
              <div className="flex items-center justify-center flex-shrink-0 w-8 h-8 text-sm font-bold text-white bg-blue-500 rounded-full prioritisedBrands-center">
                {item?.priority}
              </div>

              <div
                className={`text-gray-400 flex items-center justify-center ${
                  disabled
                    ? "cursor-not-allowed"
                    : "cursor-move hover:text-gray-600"
                }`}
              >
                <IconGripVertical size={20} />
              </div>

              <div
                className={`flex-1 font-medium flex items-center justify-left ${
                  disabled ? "text-gray-500" : "text-gray-800"
                }`}
              >
                {
                  brandOptions?.find((brand) => brand.value === item.brand)
                    ?.label
                }
              </div>

              <div className="flex gap-1">
                <button
                  onClick={(e) => moveUp(e, index)}
                  disabled={disabled || index === 0}
                  className={`p-1 disabled:opacity-30 disabled:cursor-not-allowed ${
                    disabled
                      ? "text-gray-300"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <IconChevronUp size={16} />
                </button>
                <button
                  onClick={(e) => moveDown(e, index)}
                  disabled={disabled || index === prioritisedBrands?.length - 1}
                  className={`p-1 disabled:opacity-30 disabled:cursor-not-allowed ${
                    disabled
                      ? "text-gray-300"
                      : "text-gray-400 hover:text-gray-600"
                  }`}
                >
                  <IconChevronDown size={16} />
                </button>
                <button
                  onClick={() => removeItem(item)}
                  disabled={disabled}
                  className={`p-1 disabled:cursor-not-allowed ${
                    disabled
                      ? "text-gray-300"
                      : "text-red-400 hover:text-red-600"
                  }`}
                >
                  <IconX size={16} />
                </button>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
};

export default BrandSorter;
